import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getToken, setToken, clearAuthStorage } from '../utils/authStorage';

// Auto-logout after this much inactivity (30 minutes)
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

export interface UserProject {
  projectId: number;
  projectName: string;
  roles: string[];
  permissions: string[];
}

export interface AuthUser {
  userId: number;
  email: string;
  username?: string;
  employeeName?: string;
  isSuperAdmin?: boolean;
  userProjects: UserProject[];
  allPermissions: string[];
}

interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  login: (token: string) => Promise<void>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  hasProjectAccess: (projectId: number) => boolean;
  getUserProjectPermissions: (projectId: number) => string[];
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchUserData = async (token: string): Promise<AuthUser | null> => {
    try {
      console.log('🔐 Fetching user data with token:', token ? 'Present' : 'Missing');
      
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5010/api';
      console.log('🔗 Environment API URL:', process.env.REACT_APP_API_URL);
      console.log('🔗 Using API URL:', API_URL);
      console.log('🔗 Full request URL:', `${API_URL}/auth/me`);
      
      const response = await fetch(`${API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.log('❌ Auth response not OK:', response.status, response.statusText);
        console.log('❌ Request URL:', response.url);
        console.log('❌ Request Headers:', response.headers);
        // If unauthorized, clear the token and redirect to login
        if (response.status === 401 || response.status === 403) {
          console.log('🚨 LOGOUT TRIGGERED: 401/403 response detected');
          clearAuthStorage();
          setUser(null);
          window.location.href = '/login';
        }
        throw new Error('Failed to fetch user data');
      }

      const userData = await response.json();
      console.log('✅ User data fetched successfully:', {
        username: userData.username,
        allPermissions: userData.allPermissions,
        userProjects: userData.userProjects?.length || 0
      });
      return userData;
    } catch (error) {
      console.error('❌ Error fetching user data:', error);
      // Clear token on any error
      clearAuthStorage();
      setUser(null);
      return null;
    }
  };

  const login = async (token: string) => {
    setToken(token);
    const userData = await fetchUserData(token);
    setUser(userData);
  };

  const logout = () => {
    // Wipe all auth/app data, then hard-replace so the authed page leaves the back stack
    clearAuthStorage();
    setUser(null);
    window.location.replace('/login');
  };

  const refreshUserData = async () => {
    const token = getToken();
    if (token) {
      const userData = await fetchUserData(token);
      setUser(userData);
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!user?.allPermissions) {
      console.log('❌ No permissions found for user');
      return false;
    }
    
    // Check if user has wildcard permission (super admin)
    if (user.allPermissions.includes('*') || user.allPermissions.includes('superadmin')) {
      console.log('👑 Super admin permission granted for:', permission);
      return true;
    }
    
    // Check for specific permission
    const hasSpecificPermission = user.allPermissions.includes(permission);
    console.log('🔍 Permission check:', {
      permission,
      userPermissions: user.allPermissions,
      hasPermission: hasSpecificPermission
    });
    return hasSpecificPermission;
  };

  const hasProjectAccess = (projectId: number): boolean => {
    // Super admin has access to all projects
    if (user?.allPermissions?.includes('*') || user?.allPermissions?.includes('superadmin')) {
      return true;
    }
    
    return user?.userProjects?.some(p => p.projectId === projectId) || false;
  };

  const getUserProjectPermissions = (projectId: number): string[] => {
    // Super admin has all permissions for any project
    if (user?.allPermissions?.includes('*') || user?.allPermissions?.includes('superadmin')) {
      return user?.allPermissions || [];
    }
    
    const project = user?.userProjects?.find(p => p.projectId === projectId);
    return project?.permissions || [];
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = getToken();
      if (token) {
        const userData = await fetchUserData(token);
        setUser(userData);
      }
      setLoading(false);
    };

    // Listen for token expiration events from axios interceptor
    const handleTokenExpired = () => {
      setUser(null);
    };

    window.addEventListener('auth-token-expired', handleTokenExpired);

    initAuth();

    // Cleanup event listener
    return () => {
      window.removeEventListener('auth-token-expired', handleTokenExpired);
    };
  }, []);

  // Auto-logout after a period of inactivity (only while logged in)
  useEffect(() => {
    if (!user) return;
    let timer: ReturnType<typeof setTimeout>;
    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        console.log('🔒 Auto-logout: inactivity timeout reached');
        logout();
      }, INACTIVITY_TIMEOUT_MS);
    };
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, resetTimer));
    resetTimer();
    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, resetTimer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    hasPermission,
    hasProjectAccess,
    getUserProjectPermissions,
    refreshUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
